import React, {
  CSSProperties,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { Form, Input, InputProps, TextArea } from "semantic-ui-react";

import LoadingIcon from "react-lib/apps/HISV3/HCU/LoadingIcon";

type EditorColumnProps = {
  id?: any;
  className?: string;
  style?: CSSProperties;
  align?: CSSProperties["textAlign"];
  backgroundColor?: CSSProperties["background"];
  disabled?: boolean;
  editing?: boolean;
  editOnTrigger?: boolean;
  inputMin?: any;
  inputNumber?: boolean;
  inputSize?: InputProps["size"];
  inputStyle?: CSSProperties;
  inputType?: string;
  loading?: boolean;
  padding?: CSSProperties["padding"];
  selectOnFocus?: boolean;
  triggerDimmerStyle?: CSSProperties;
  triggerLoading?: boolean;
  triggerStyle?: CSSProperties;
  type?: "input" | "textarea";
  value: any;
  valueOnFocus?: boolean;
  withoutStyle?: boolean;
  onDataChanged?: (value: string) => any;
};

const ColumnStyle: CSSProperties = {
  background: "rgb(255, 255, 204)",
  height: "calc(100% + 14px)",
  marginLeft: -4,
  marginTop: -7,
  padding: "7px 7px",
  width: "calc(100% + 10px)",
};

const EditorColumn = (props: EditorColumnProps) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (props.editing && !props.loading) {
      handleEdit();
    }
  }, [props.editing, props.loading, props.value]);

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();

    if (props.disabled) {
      setIsEdit(false);
    } else if (!props.editOnTrigger) {
      handleEdit(true);
    }
  };

  const handleBlur = () => {
    const value = (inputRef.current?.inputRef || inputRef.current?.ref)?.current?.value || "";

    if (!props.editOnTrigger) {
      props.onDataChanged?.(value);
    }

    if (!props.editing) {
      setIsEdit(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const elm = (inputRef.current?.inputRef || inputRef.current?.ref)
        ?.current as HTMLInputElement | null;
      const value = elm?.value || "";

      if (props.editing) {
        elm?.blur();

        return;
      }

      props.onDataChanged?.(value);

      setIsEdit(false);
    }

    if (props.inputNumber && !/\d/.test(e.key) && e.key !== "Backspace" && e.key !== ".") {
      e.preventDefault();
    }
  };

  const handleEdit = (isClick = false) => {
    setIsEdit(true);

    setTimeout(() => {
      const elm = (inputRef.current?.inputRef || inputRef.current?.ref)
        ?.current as HTMLInputElement | null;

      if (props.valueOnFocus && elm) {
        const value = elm.value;

        elm.value = isClick ? value : props.value ?? value;
      }

      if (!props.editing) {
        elm?.focus();
      }

      if (props.selectOnFocus) {
        elm?.select();
      }
    }, 100);
  };

  return (
    <div
      style={{
        ...(props.withoutStyle ? {} : { ...ColumnStyle, background: props.backgroundColor }),
        ...props.style,
      }}
      onClick={props.withoutStyle ? undefined : handleClick}
      onDoubleClick={handleClick}
    >
      {isEdit ? (
        <Form>
          {props.type === "textarea" ? (
            <TextArea
              id={props.id ?? `EditorColumn-TextArea`}
              ref={inputRef}
              style={{ width: "100%" }}
              disabled={props.disabled}
              rows={3}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <Input
              id={props.id ?? `EditorColumn-Input`}
              ref={(ref: any) => {
                if (ref) {
                  inputRef.current = ref;

                  if (!props.align) {
                    ref.inputRef.current.style.textAlign = "right";
                  }
                }
              }}
              className={props.className}
              style={{ width: "100%", ...props.inputStyle }}
              disabled={props.disabled}
              loading={props.loading}
              min={props.inputMin}
              size={props.inputSize}
              type={props.inputType}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          )}
        </Form>
      ) : (
        <div
          style={{
            padding: props.padding,
            textAlign: props.align || "right",
            ...(props.editOnTrigger && { display: "flex" }),
          }}
        >
          <span style={{ width: "100%" }}>{props.value}</span>
          {props.editOnTrigger && !props.disabled && (
            <LoadingIcon
              style={{ float: "right", ...props.triggerStyle }}
              color="yellow"
              dimmerStyle={props.triggerDimmerStyle}
              loading={props.triggerLoading}
              name="edit outline"
              onClick={() => {
                handleEdit();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

EditorColumn.displayName = "EditorColumn";

export default React.memo(EditorColumn);
